import { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { startOfDay } from "date-fns";

import { classNames } from "@/utils/classNames";
import { useIsFeatureActive } from "@/utils/useIsFeatureActive";

import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { useSearch } from "@/utils";
import { listSelector, setCurrentLeadIndex } from "@/pages/Private/redux/list/list.slice";
import { ListSourceType } from "@/enum/list.enum";

import { useGetSendersUsersQuery } from "@/pages/Private/redux/admin/admin.api";
import { useGetCompanyLifecycleQuery } from "@/pages/Private/redux/company/company.api";

import { authSelector } from "@/pages/Public/redux/auth.slice";

import { NoSenderModal } from "@/components/NoSenderModal/NoSenderModal";

import { MessagesFilterBar } from "../../components/MessagesFilterBar/MessagesFilterBar";

import { FeaturesCodes } from "../../../Users/enum/codes";
import { GlobalNodesDataProvider } from "../../../List/components/CampaignOverview/components/NodesContextProvider";
import { FilterModal } from "../../../List/components/FilterModal";

import { ListPerson } from "../../../List/schema/list";
import { CampaignMessagingOverview } from "../CampaignMessagingOverview/CampaignMessagingOverview";
import { LeadDetailRail } from "../../components/LeadDetailRail";
import { SelectedLeadsOverlay } from "../../components/SelectedLeadsOverlay";
import { useFeedDraftGeneration } from "../../../List/components/CampaignOverview/utils/useFeedDraftGeneration";
import { OutboxChatTab } from "./OutboxChatTab";
import { OutboxNoCampaignSignpost } from "./OutboxNoCampaignSignpost";
import { CustomListsAutocompleteForSequencerItem } from "../../../List/components/CustomListsAutocompleteForSequencer";
import { ListContextHeader } from "../../components/ListContextHeader";
import { ListScopeFilter } from "../../components/ListScopeFilter/ListScopeFilter";
import { ListScope } from "../../components/ListScopeFilter/listScope";
import { useListScopeLists } from "../../components/ListScopeFilter/useListScopeLists";
import { ReadOnlyPlanBanner } from "../../components/ReadOnlyPlanBanner";
import { resolveMessagesAccessMode } from "../../utils/messagesAccessMode";
import { CombinedSignalFeed } from "../../../ProfileTracker/components/CustomColumns/SignalTimeline";
import { prospectHasFeedSignal } from "../../../ProfileTracker/components/CustomColumns/SignalTimeline/combinedSignalFeedUtils";
import { resolveOutboxAutoTab } from "../../utils/resolveOutboxAutoTab";
import { useCampaignStatusPolling } from "../../../List/hooks/useCampaignStatusPolling";
import { Tabs } from "./outboxTabs";
import { useOutboxUrlState } from "./useOutboxUrlState";
import { useOutboxTabScope } from "./useOutboxTabScope";
import { LAST_MESSAGE_SENT_SORT_KEY } from "./outboxStatusTabs";
import { OutboxLeadCountLabel } from "./OutboxLeadCountLabel";
import { OutboxActionBar } from "./OutboxActionBar";
import { OutboxFilterDropdowns } from "./OutboxFilterDropdowns";
import { useOutboxCampaignScope } from "./useOutboxCampaignScope";
import { OutboxLeadsColumn } from "./OutboxLeadsColumn";
import { OutboxViewTabs } from "./OutboxViewTabs";
import { useOutboxProspects } from "./useOutboxProspects";
import { useOutboxCampaignConfig } from "./useOutboxCampaignConfig";

export const Outbox: FunctionComponent = () => {
	const { t } = useTranslation();

	const { type = "outbox" } = useParams();

	const { user } = useAppSelector(authSelector);
	const sendButtonsPortalRef = useRef<HTMLDivElement>(null);

	const {
		lists: listData,
		filterProps: listFilterProps,
		resetPage: resetListPage,
	} = useListScopeLists(ListScope.OUTBOX);

	const { data: sendingUsers } = useGetSendersUsersQuery(undefined, {
		refetchOnMountOrArgChange: true,
	});
	const { data: lifecycle } = useGetCompanyLifecycleQuery();
	const accessMode = resolveMessagesAccessMode({
		lifecycleState: lifecycle?.state,
		hasSendingUsers: (sendingUsers?.data?.length ?? 0) > 0,
		sendingUsersLoaded: Boolean(sendingUsers?.data),
	});

	const doesSendingUsersHaveMe = useMemo(() => {
		return sendingUsers?.data?.some((sending) => sending.id === user?.id);
	}, [sendingUsers, user]);

	const location = useLocation();
	const { limit, query, setSearchValue, searchValue } = useSearch();

	const {
		currentTab,
		setCurrentTab,
		statusTab,
		setStatusTab,
		allInclude,
		toggleInclude,
		selectedLists,
		setSelectedLists,
		selectedSenders,
		setSelectedSenders,
		selectedCampaignIds,
		setSelectedCampaignIds,
		outboxSource,
		setOutboxSource,
		filter,
		setFilter,
	} = useOutboxUrlState({
		user,
		sendingUsersLoaded: !!sendingUsers,
		doesSendingUsersHaveMe,
		searchValue,
		setSearchValue,
	});

	const { campaignOptions, applyLists } = useOutboxCampaignScope({
		selectedLists,
		setSelectedLists,
		setSelectedCampaignIds,
	});

	const { sortingInbox, inbox, currentLeadIndex, meta } = useAppSelector(listSelector);

	const [selected, setSelected] = useState<number[]>([]);

	const timeoutRef = useRef<NodeJS.Timeout | null>(null);

	const navigate = useNavigate();
	const dispatch = useAppDispatch();

	const { outboxTabsScope, resolvedSorting, outboxCounts } = useOutboxTabScope({
		statusTab,
		allInclude,
		locationSearch: location.search,
		sortingInbox,
		selectedSenders,
		selectedLists,
		query,
		filter,
		campaignIds: selectedCampaignIds,
		outboxSource: outboxSource === "all" ? undefined : outboxSource,
		skip: !sendingUsers,
	});

	const {
		resetPage,
		leadsEndRef,
		isFetching: isFetchingGetUnassigned,
		isLoading: isLoadingGetUnassigned,
		hasMorePages,
	} = useOutboxProspects({
		sendingUsersLoaded: !!sendingUsers,
		filter,
		type,
		query,
		limit,
		selectedSenders,
		selectedLists,
		outboxTabs: outboxTabsScope,
		campaignIds: selectedCampaignIds,
		outboxSource: outboxSource === "all" ? undefined : outboxSource,
		sorting: resolvedSorting,
	});

	const {
		selectedCampaignConfig,
		customCampaignConfig,
		setCustomCampaignConfig,
		isDirty,
		setIsDirty,
		isValidCustomCampaignConfig,
		isUpdatingListPerson,
		isLoading,
		handleSaveCustom,
	} = useOutboxCampaignConfig({ inbox, currentLeadIndex });

	const [showFilterModal, setShowFilterModal] = useState<boolean>(false);

	const [showLeads, setShowLeads] = useState<boolean>(true);
	// Open by default (PREC-3850): the docked rail reserves its own width.
	const [showSingleLead, setShowSingleLead] = useState<boolean>(true);
	const { generateDraftsForLead, renderFeedDraftSnackbar } = useFeedDraftGeneration();
	const handleReachOutFromSignal = useCallback(() => {
		setCurrentTab(Tabs.CHAT);
		setShowSingleLead(true);
	}, []);

	const handleGenerateDraftsFromSignal = useCallback(
		(inboxIndex: number, signal: { summary?: string | null; title?: string | null }) => {
			generateDraftsForLead({
				inboxIndex,
				triggerDescription: signal.summary || signal.title || null,
			});
		},
		[generateDraftsForLead]
	);

	const isSequencerActive = useIsFeatureActive(FeaturesCodes.SEQUENCER);

	useCampaignStatusPolling();

	function handleSetCurrentLead(index: number) {
		dispatch(setCurrentLeadIndex(index));
	}

	useEffect(() => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
		}

		timeoutRef.current = setTimeout(() => {
			if (!isSequencerActive) {
				navigate(`/app/lists/`);
			}
		}, 500);
	}, [isSequencerActive, navigate]);

	const prevLeadIndexRef = useRef<number | undefined>(undefined);

	useEffect(() => {
		const prospect = inbox[currentLeadIndex];

		if (!prospect) {
			return;
		}

		// Don't auto-switch away from the Feed tab while the user browses a prospect's signals there.
		if (currentTab === Tabs.FEED && prospectHasFeedSignal(prospect)) {
			return;
		}

		if (prevLeadIndexRef.current === currentLeadIndex && currentTab !== undefined) {
			return;
		}

		prevLeadIndexRef.current = currentLeadIndex;

		setCurrentTab(resolveOutboxAutoTab(prospect));
	}, [inbox, currentLeadIndex]);

	const currentListPerson: ListPerson | undefined = useMemo(() => {
		if (currentLeadIndex === undefined || currentLeadIndex < 0) {
			return undefined;
		}

		return inbox[currentLeadIndex];
	}, [inbox, currentLeadIndex]);

	const todayStart = useMemo(() => startOfDay(new Date()), []);

	const listFilterElement = (
		<div className="min-w-[125px] w-[fit-content]">
			<ListScopeFilter
				{...listFilterProps}
				onApply={(value: CustomListsAutocompleteForSequencerItem[]) => {
					applyLists(value);
					resetListPage();
				}}
				selectedLists={selectedLists || []}
			/>
		</div>
	);

	return (
		<>
			{accessMode === "no-senders" && <NoSenderModal />}
			<div
				className={classNames(
					"sm:block flex flex-col items-start justify-between w-full  h-full",
					accessMode === "no-senders" ? "blur-sm" : ""
				)}
			>
				<div className="bg-brand-white  flex flex-col items-stretch w-full h-screen">
					{accessMode === "read-only" && (
						<ReadOnlyPlanBanner readOnlyUntil={lifecycle?.readOnlyUntil ?? null} />
					)}
					<GlobalNodesDataProvider>
						<ListContextHeader
							lists={listData}
							listFilter={listFilterElement}
							selectedLists={selectedLists}
							title={t("messages.pageTitle.outbox")}
						/>
						{/* Full-height docked rail (PREC-3850): the rail sits beside the tab strip,
						    filter bar and columns, spanning from the context header to the bottom. */}
						<div className="flex flex-row flex-1 min-h-0 w-full">
							<div className="flex flex-col flex-1 min-w-0">
								<OutboxViewTabs
									allInclude={allInclude}
									counts={outboxCounts}
									currentTab={currentTab}
									hasConversations={inbox[currentLeadIndex]?.prospect?.conversations?.length !== 0}
									showFeedTab={inbox.some(
										(lp) =>
											(lp.prospect?.organization?.trackerSignalResults?.length ?? 0) > 0 ||
											(lp.prospect?.trackerSignalResults?.length ?? 0) > 0
									)}
									statusTab={statusTab}
									onStatusTabChange={(tab) => {
										setStatusTab(tab);
										resetPage();
									}}
									onTabChange={setCurrentTab}
									onToggleInclude={toggleInclude}
								/>
								<MessagesFilterBar
									filterCount={filter ? Object.keys(filter).length : 0}
									sortExtraOptions={[
										{
											orderTitle: LAST_MESSAGE_SENT_SORT_KEY,
											title: t("outbox.sortByOptions.lastMessageSent"),
										},
									]}
									onOpenFilter={() => setShowFilterModal(true)}
									searchValue={searchValue}
									onSearchChange={(value) => {
										setSearchValue(value);
										resetPage();
									}}
									dropdowns={
										<OutboxFilterDropdowns
											doesSendingUsersHaveMe={doesSendingUsersHaveMe}
											selectedCampaignIds={selectedCampaignIds}
											campaignOptions={campaignOptions}
											selectedSenders={selectedSenders}
											sendingUsers={sendingUsers?.data}
											setSelectedCampaignIds={setSelectedCampaignIds}
											setSelectedSenders={setSelectedSenders}
											user={user}
											outboxSource={outboxSource}
											setOutboxSource={setOutboxSource}
											onBeforeChange={resetPage}
										/>
									}
									className="border-b border-border-light"
									trailing={<OutboxLeadCountLabel count={meta.itemCount} />}
								/>
								<div
									className="flex w-full flex-1 min-h-0 flex-row relative overflow-hidden"
									// overflow-hidden boxes can still be scrolled programmatically
									// (scrollIntoView, focus scrolling) and the user can never scroll
									// them back — pin the position so the filter/sender controls don't
									// get stuck clipped under the tab bar.
									onScroll={(e) => {
										e.currentTarget.scrollTop = 0;
										e.currentTarget.scrollLeft = 0;
									}}
								>
									<OutboxLeadsColumn
										handleSetCurrentLead={handleSetCurrentLead}
										hasSearchQuery={!!query}
										selectedLists={selectedLists}
										statusTab={statusTab}
										hasMorePages={hasMorePages}
										isFetching={isFetchingGetUnassigned}
										leadsEndRef={leadsEndRef}
										rows={inbox}
										selected={selected}
										setSelected={setSelected}
										showLeads={showLeads}
										todayStart={todayStart}
										onToggleShowLeads={() => setShowLeads(!showLeads)}
									/>

									{/* min-w-0: a flex item never shrinks below its content by default, so on a
									    laptop the chat kept its width and the row's overflow-hidden cut off its
									    right edge — the Send button included (PREC-4217). */}
									<div
										className={classNames(
											"h-full min-h-[100%] w-full min-w-0 z-5 relative flex flex-col"
										)}
									>
										{selected.length > 0 && <SelectedLeadsOverlay />}
										<div
											className={classNames(
												"flex-1 min-h-0 w-full z-5 relative",
												selected.length > 0 ? "blur" : ""
											)}
										>
											{currentTab === Tabs.CAMPAIGN && (
												<div className="w-full h-full">
													{inbox?.length > 0 &&
														!isLoading &&
														!isLoadingGetUnassigned &&
														(selectedCampaignConfig ? (
															// On a campaign or covered by a list default → show the sequence.
															<CampaignMessagingOverview
																campaignConfigRaw={selectedCampaignConfig || null}
																isDirty={isDirty}
																listPerson={inbox[currentLeadIndex]}
																setCustomCampaignConfig={setCustomCampaignConfig}
																setIsDirty={setIsDirty}
															/>
														) : (
															// No campaign behind this lead — their messages went or go
															// out individually via Chat, so don't render an empty sequence.
															<OutboxNoCampaignSignpost
																onOpenChat={() => setCurrentTab(Tabs.CHAT)}
															/>
														))}
												</div>
											)}
											{currentTab === Tabs.CHAT && (
												<OutboxChatTab
													isLoadingGetUnassigned={isLoadingGetUnassigned}
													selected={selected}
													sendButtonsPortalRef={sendButtonsPortalRef}
												/>
											)}
											{currentTab === Tabs.FEED && (
												<div className="w-full h-full pr-12">
													<CombinedSignalFeed
														onGenerateDraftMessages={handleGenerateDraftsFromSignal}
														onReachOutToProspect={handleReachOutFromSignal}
													/>
												</div>
											)}
										</div>
										<OutboxActionBar
											accessMode={accessMode}
											currentListPerson={currentListPerson}
											currentTab={currentTab}
											isSaveCustomDisabled={!isValidCustomCampaignConfig}
											isSavingCustom={isUpdatingListPerson}
											sendButtonsPortalRef={sendButtonsPortalRef}
											showSaveCustom={selected.length === 0 && !!customCampaignConfig && isDirty}
											onSaveCustom={() => handleSaveCustom(customCampaignConfig)}
										/>
									</div>
								</div>
							</div>
							<LeadDetailRail
								docked
								listPerson={currentListPerson}
								setShow={setShowSingleLead}
								show={showSingleLead}
								// The panel has no Chat tab of its own here, but this page does — so
								// "Reach out to {name}" opens it instead of sitting greyed out (PREC-4107).
								onReachOut={() => setCurrentTab(Tabs.CHAT)}
							/>
						</div>
					</GlobalNodesDataProvider>

					{showFilterModal && (
						<FilterModal
							filter={filter}
							listType={ListSourceType.CSV_ENRICHMENTS}
							setFilter={(e) => {
								resetPage();
								setFilter(e);
							}}
							setShowFilterModal={setShowFilterModal}
							showFilterModal={showFilterModal}
						/>
					)}
				</div>

				{renderFeedDraftSnackbar()}
			</div>
		</>
	);
};
